import { ConnectionPointType } from "../connection/connectionPointType.js";
import { BaseBlock } from "./baseBlock.js";
import { CreateStrongRef } from "../runtime/strongRef.js";
/**
 * Type predicate to check if value is a strong ref or a direct value
 * @param value - The value to check
 * @returns true if the value is a strong ref, otherwise false
 */
function IsRuntimeData(value) {
    return value && value.value !== undefined;
}
/**
 * Predicate to check if a block is a texture input block.
 * @param block - The block to check
 * @returns true if the block is a texture input block, otherwise false
 */
export function IsTextureInputBlock(block) {
    return block.type === ConnectionPointType.Texture;
}
/**
 * Predicate to check if a block is a disableable block.
 * @param block - The block to check
 * @returns true if the block is a disableable block, otherwise false
 */
export function IsDisableableShaderBlock(block) {
    return block.disabled !== undefined;
}
/**
 * This base class exists to provide a type that the serializer can use to represent
 * any InputBlock without knowing the exact type it is.
 */
export class InputBlockBase extends BaseBlock {
}
/**
 * The class name of the block.
 */
InputBlockBase.ClassName = "InputBlock";
/**
 * This represents any inputs used in the graph.
 *
 * This is used to provide a way to connect the graph to the outside world.
 *
 * The value is dynamically set by the user.
 */
export class InputBlock extends InputBlockBase {
    /**
     * Gets the current value of the input.
     */
    get runtimeValue() {
        return this.output.runtimeData;
    }
    /**
     * Sets the current value of the input.
     */
    set runtimeValue(value) {
        this.output.runtimeData = value;
    }
    /**
     * Creates a new InputBlock.
     * @param smartFilter - The smart filter to add the block to
     * @param name - The friendly name of the block
     * @param type - The type of the input
     * @param initialValue - The initial value of the input
     * @remarks the initial value can either be a strong reference or a value
     */
    constructor(smartFilter, name, type, initialValue) {
        super(smartFilter, name);
        /**
         * Data used by the Editor to store options required for instantiating the block in the Editor.
         */
        this.editorData = null;
        /**
         * Metadata the hosting app wants to track for this input. For example, a hint for what data to
         * assign to this input, or hints about how to draw dynamic UI to allow users to control this value.
         */
        this.appMetadata = null;
        this.type = type;
        // Creates the output connection point
        this.output = this._registerOutputWithDefault("output", type, IsRuntimeData(initialValue) ? initialValue : CreateStrongRef(initialValue));
        // Creates a strong reference to the initial value in case a reference has not been provided
        if (IsRuntimeData(initialValue)) {
            this.runtimeValue = initialValue;
        }
        else {
            this.runtimeValue = CreateStrongRef(initialValue);
        }
    }
}
//# sourceMappingURL=inputBlock.js.map